import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import dayjs from 'dayjs';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import {
    Card,
    Row,
    Col,
    Table,
    Tag,
    Space,
    Typography,
    Button,
    Modal,
    Input,
    InputNumber,
    DatePicker,
    Select,
    Segmented,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    SwapOutlined,
    ArrowRightOutlined,
    PrinterOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

export default function BankTransfer() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);

    const [formData, setFormData] = useState({
        from_account_id: '',
        to_account_id: '',
        amount: '',
        transfer_date: new Date().toISOString().split('T')[0],
        description: '',
        transfer_charges: 0,
        transfer_type: 'transfer' // 'transfer', 'cash_deposit', 'cash_withdrawal'
    });

    const transferTypes = [
        { value: 'transfer', label: 'Bank to Bank Transfer' },
        { value: 'cash_deposit', label: 'Cash Deposit to Bank' },
        { value: 'cash_withdrawal', label: 'Cash Withdrawal from Bank' }
    ];

    useEffect(() => {
        fetchBankAccounts();
        fetchTransfers();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            // console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchTransfers = async () => {
        setLoading(true);
        try {
            await fetchData("bank_transfers", setTransfers, "id", {});
        } catch (error) {
            // console.error('Error fetching transfers:', error);
            toast.error('Failed to fetch bank transfers');
        }
        setLoading(false);
    };

    const resetForm = () => {
        setFormData({
            from_account_id: '',
            to_account_id: '',
            amount: '',
            transfer_date: new Date().toISOString().split('T')[0],
            description: '',
            transfer_charges: 0,
            transfer_type: 'transfer'
        });
    };

    const handleTransferTypeChange = useCallback((transferType) => {
        setFormData(prev => ({
            ...prev,
            transfer_type: transferType,
            from_account_id: '',
            to_account_id: '',
            description: transferType === 'cash_deposit' ? 'Cash deposit to bank account' :
                        transferType === 'cash_withdrawal' ? 'Cash withdrawal from bank account' : ''
        }));
    }, []);

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Validation based on transfer type
        if (formData.transfer_type === 'transfer') {
            if (!formData.from_account_id || !formData.to_account_id) {
                toast.error('Please select both source and destination accounts');
                return;
            }

            if (formData.from_account_id === formData.to_account_id) {
                toast.error('Source and destination accounts cannot be the same');
                return;
            }
        } else if (formData.transfer_type === 'cash_deposit') {
            if (!formData.to_account_id) {
                toast.error('Please select bank account for deposit');
                return;
            }
            const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');
            if (!cashAccount) {
                toast.error('Cash account not found. Please create a cash account first.');
                return;
            }
            formData.from_account_id = cashAccount.id;
        } else if (formData.transfer_type === 'cash_withdrawal') {
            if (!formData.from_account_id) {
                toast.error('Please select bank account for withdrawal');
                return;
            }
            const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');
            if (!cashAccount) {
                toast.error('Cash account not found. Please create a cash account first.');
                return;
            }
            formData.to_account_id = cashAccount.id;
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        const sourceAccount = bankAccounts.find(acc => acc.id === parseInt(formData.from_account_id));
        if (sourceAccount && sourceAccount.current_balance < parseFloat(formData.amount)) {
            toast.error('Insufficient balance in source account');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `/bank-transfers`,
                {
                    ...formData,
                    transfer_type: formData.transfer_type
                },
                getHeaders()
            );

            if (response.data.success) {
                const successMessage = formData.transfer_type === 'cash_deposit' ? 'Cash deposited successfully' :
                                     formData.transfer_type === 'cash_withdrawal' ? 'Cash withdrawn successfully' :
                                     'Bank transfer completed successfully';
                toast.success(successMessage);
                fetchTransfers();
                fetchBankAccounts();
                setShowAddModal(false);
                resetForm();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to complete transfer');
        }
        setLoading(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getAccountInfo = useCallback((accountId) => {
        const account = bankAccounts.find(acc => acc.id === accountId);
        return account ? {
            name: account.account_name,
            bank: account.bank_name,
            number: account.account_number,
            balance: account.current_balance
        } : null;
    }, [bankAccounts]);

    const handlePrintReceipt = (transfer) => {
        const fromAccount = getAccountInfo(transfer.from_account_id);
        const toAccount = getAccountInfo(transfer.to_account_id);
        const transferDate = new Date(transfer.transfer_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        const transferTypeName = transfer.transfer_type === 'cash_deposit' ? 'Cash Deposit' :
                                transfer.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal' :
                                'Bank Transfer';

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${transferTypeName} Slip</title>
                <style>
                    @page {
                        size: A4;
                        margin: 12mm;
                    }

                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }

                    body {
                        font-family: "Inter", "Segoe UI", Arial, sans-serif;
                        font-size: 12px;
                        line-height: 1.5;
                        color: #111827;
                        background: #ffffff;
                    }

                    .slip {
                        max-width: 720px;
                        margin: 0 auto;
                        border: 1px solid #e5e7eb;
                        border-radius: 12px;
                        padding: 24px;
                    }

                    .header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding-bottom: 16px;
                        border-bottom: 1px dashed #d1d5db;
                        margin-bottom: 18px;
                    }

                    .brand {
                        font-weight: 700;
                        font-size: 18px;
                        letter-spacing: 0.3px;
                    }

                    .badge {
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.6px;
                        padding: 6px 10px;
                        border-radius: 999px;
                        background: #f3f4f6;
                        color: #374151;
                        border: 1px solid #e5e7eb;
                    }

                    .meta {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 12px;
                        margin-bottom: 18px;
                    }

                    .meta-card {
                        border: 1px solid #e5e7eb;
                        border-radius: 10px;
                        padding: 10px 12px;
                        background: #fafafa;
                    }

                    .meta-label {
                        font-size: 10px;
                        color: #6b7280;
                        margin-bottom: 4px;
                    }

                    .meta-value {
                        font-weight: 600;
                        color: #111827;
                    }

                    .accounts {
                        display: grid;
                        grid-template-columns: 1fr 40px 1fr;
                        gap: 10px;
                        margin: 16px 0 22px;
                        align-items: center;
                    }

                    .account {
                        border: 1px solid #e5e7eb;
                        border-radius: 12px;
                        padding: 12px;
                        background: #ffffff;
                    }

                    .account-title {
                        font-size: 11px;
                        color: #6b7280;
                        margin-bottom: 6px;
                    }

                    .account-name {
                        font-weight: 700;
                        margin-bottom: 2px;
                    }

                    .account-sub {
                        color: #6b7280;
                        font-size: 11px;
                    }

                    .arrow {
                        text-align: center;
                        font-size: 18px;
                        color: #9ca3af;
                    }

                    .amount {
                        border: 1px solid #111827;
                        border-radius: 12px;
                        padding: 16px;
                        text-align: center;
                        margin-bottom: 14px;
                    }

                    .amount-label {
                        font-size: 11px;
                        letter-spacing: 0.4px;
                        color: #6b7280;
                        margin-bottom: 6px;
                    }

                    .amount-value {
                        font-size: 26px;
                        font-weight: 800;
                        color: #111827;
                    }

                    .charges {
                        margin-top: 8px;
                        font-size: 11px;
                        color: #6b7280;
                    }

                    .note {
                        border: 1px dashed #e5e7eb;
                        border-radius: 10px;
                        padding: 10px 12px;
                        margin-top: 10px;
                        color: #374151;
                        font-size: 11px;
                    }

                    .footer {
                        margin-top: 18px;
                        padding-top: 12px;
                        border-top: 1px dashed #e5e7eb;
                        display: flex;
                        justify-content: space-between;
                        font-size: 10px;
                        color: #6b7280;
                    }

                    @media print {
                        body { margin: 0; }
                        .slip { border: none; }
                    }
                </style>
            </head>
            <body>
                <div class="slip">
                    <div class="header">
                        <div class="brand">Transaction Slip</div>
                        <div class="badge">${transferTypeName}</div>
                    </div>

                    <div class="meta">
                        <div class="meta-card">
                            <div class="meta-label">Transfer ID</div>
                            <div class="meta-value">TXN-${transfer.id}</div>
                        </div>
                        <div class="meta-card">
                            <div class="meta-label">Date</div>
                            <div class="meta-value">${transferDate}</div>
                        </div>
                        <div class="meta-card">
                            <div class="meta-label">Printed</div>
                            <div class="meta-value">${new Date().toLocaleString()}</div>
                        </div>
                    </div>

                    <div class="accounts">
                        <div class="account">
                            <div class="account-title">From</div>
                            <div class="account-name">${fromAccount?.name || 'Cash in Hand'}</div>
                            <div class="account-sub">${fromAccount?.bank || 'N/A'} • ${fromAccount?.number || 'N/A'}</div>
                        </div>
                        <div class="arrow">➜</div>
                        <div class="account">
                            <div class="account-title">To</div>
                            <div class="account-name">${toAccount?.name || 'Cash in Hand'}</div>
                            <div class="account-sub">${toAccount?.bank || 'N/A'} • ${toAccount?.number || 'N/A'}</div>
                        </div>
                    </div>

                    <div class="amount">
                        <div class="amount-label">Transfer Amount</div>
                        <div class="amount-value">${formatCurrency(transfer.amount)}</div>
                        ${transfer.transfer_charges > 0 ? `
                            <div class="charges">Transfer Charges: ${formatCurrency(transfer.transfer_charges)}</div>
                        ` : ''}
                    </div>

                    ${transfer.description ? `
                        <div class="note">
                            <strong>Note:</strong> ${transfer.description}
                        </div>
                    ` : ''}

                    <div class="footer">
                        <div>This is a system-generated slip.</div>
                        <div>No signature required.</div>
                    </div>
                </div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
            }, 250);
        };
    };

    // Memoized transfer summary to prevent rerender on every keystroke
    const TransferSummary = useMemo(() => {
        const sourceAccount = getAccountInfo(parseInt(formData.from_account_id));
        const destinationAccount = getAccountInfo(parseInt(formData.to_account_id));

        const summaryTitle = formData.transfer_type === 'cash_deposit' ? 'Cash Deposit Summary' :
            formData.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal Summary' :
            'Transfer Summary';

        const fromLabel = formData.transfer_type === 'cash_deposit'
            ? { name: 'Cash in Hand', sub: 'Physical Cash' }
            : { name: sourceAccount?.name, sub: sourceAccount ? `${sourceAccount.bank} - ${sourceAccount.number}` : '' };

        const toLabel = formData.transfer_type === 'cash_withdrawal'
            ? { name: 'Cash in Hand', sub: 'Physical Cash' }
            : { name: destinationAccount?.name, sub: destinationAccount ? `${destinationAccount.bank} - ${destinationAccount.number}` : '' };

        const showAccounts =
            (formData.transfer_type === 'transfer' && formData.from_account_id && formData.to_account_id) ||
            (formData.transfer_type === 'cash_deposit' && formData.to_account_id) ||
            (formData.transfer_type === 'cash_withdrawal' && formData.from_account_id);

        return (
            <Card style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, marginTop: 8 }}>
                <Text strong style={{ color: '#0c4a6e' }}>{summaryTitle}</Text>

                {!formData.amount && (
                    <div style={{ color: '#64748b', fontStyle: 'italic', marginTop: 8 }}>
                        Enter amount to see transfer summary
                    </div>
                )}

                {formData.amount && showAccounts && (
                    <Row align="middle" style={{ marginTop: 12 }}>
                        <Col xs={24} sm={10}>
                            <Text type="secondary" style={{ fontSize: 12 }}>FROM</Text>
                            <div><Text strong>{fromLabel.name}</Text></div>
                            <Text type="secondary" style={{ fontSize: 12 }}>{fromLabel.sub}</Text>
                        </Col>
                        <Col xs={24} sm={4} style={{ textAlign: 'center' }}>
                            <ArrowRightOutlined style={{ fontSize: 20, color: '#0284c7' }} />
                        </Col>
                        <Col xs={24} sm={10}>
                            <Text type="secondary" style={{ fontSize: 12 }}>TO</Text>
                            <div><Text strong>{toLabel.name}</Text></div>
                            <Text type="secondary" style={{ fontSize: 12 }}>{toLabel.sub}</Text>
                        </Col>
                    </Row>
                )}

                {formData.amount && (
                    <>
                        <div style={{ borderTop: '1px solid #bae6fd', margin: '12px 0' }} />
                        <Row justify="space-between">
                            <Text>
                                {formData.transfer_type === 'cash_deposit' ? 'Deposit Amount:' :
                                 formData.transfer_type === 'cash_withdrawal' ? 'Withdrawal Amount:' :
                                 'Transfer Amount:'}
                            </Text>
                            <Text strong>{formatCurrency(parseFloat(formData.amount) || 0)}</Text>
                        </Row>
                        {formData.transfer_charges > 0 && (
                            <Row justify="space-between">
                                <Text>Transfer Charges:</Text>
                                <Text strong>{formatCurrency(formData.transfer_charges)}</Text>
                            </Row>
                        )}
                    </>
                )}
            </Card>
        );
    }, [formData.amount, formData.from_account_id, formData.to_account_id, formData.transfer_type, formData.transfer_charges, getAccountInfo]);

    const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');

    const transferColumns = [
        { title: 'Transfer ID', dataIndex: 'transfer_id', key: 'transfer_id', render: (v) => <Text strong>{v}</Text> },
        { title: 'Date', dataIndex: 'transfer_date', key: 'date', render: (v) => formatDate(v) },
        {
            title: 'Type',
            dataIndex: 'transfer_type',
            key: 'type',
            render: (v) => (
                <Tag color={v === 'cash_deposit' ? 'green' : v === 'cash_withdrawal' ? 'orange' : 'blue'}>
                    {v === 'cash_deposit' ? 'Cash Deposit' : v === 'cash_withdrawal' ? 'Cash Withdrawal' : 'Bank Transfer'}
                </Tag>
            )
        },
        {
            title: 'From Account',
            key: 'from',
            render: (_, transfer) => {
                if (transfer.transfer_type === 'cash_deposit') {
                    return <div><Text strong>Cash</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Physical Cash</Text></div>;
                }
                const fromAccount = getAccountInfo(transfer.from_account_id);
                return fromAccount ? <div><Text strong>{fromAccount.name}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{fromAccount.bank}</Text></div> : '-';
            }
        },
        {
            title: 'To Account',
            key: 'to',
            render: (_, transfer) => {
                if (transfer.transfer_type === 'cash_withdrawal') {
                    return <div><Text strong>Cash</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Physical Cash</Text></div>;
                }
                const toAccount = getAccountInfo(transfer.to_account_id);
                return toAccount ? <div><Text strong>{toAccount.name}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{toAccount.bank}</Text></div> : '-';
            }
        },
        { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (v) => <Text strong style={{ color: '#0891b2' }}>{formatCurrency(v)}</Text> },
        { title: 'Charges', dataIndex: 'transfer_charges', key: 'charges', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
        { title: 'Description', dataIndex: 'description', key: 'description', render: (v) => v || '-', ellipsis: true },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, transfer) => (
                <Tooltip title="Print Receipt">
                    <Button size="small" type="primary" icon={<PrinterOutlined />} onClick={() => handlePrintReceipt(transfer)}>Print</Button>
                </Tooltip>
            )
        },
    ];

    return (
        <Layout>
            <Header title="Bank Transfers" />
            <ToastContainer />

            <div style={{ padding: 20 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Title level={4} style={{ margin: 0 }}>
                            <SwapOutlined style={{ marginRight: 8, color: '#0d9488' }} />
                            Bank Transfers &amp; Cash Management
                        </Title>
                    </Col>
                    <Col>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { resetForm(); setShowAddModal(true); }}>
                            New Transaction
                        </Button>
                    </Col>
                </Row>

                {/* Account Balance Summary */}
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    {bankAccounts.filter(acc => acc.is_active).map((account, idx) => {
                        const palette = ['#f0fdfa', '#eff6ff', '#fffbeb', '#fdf4ff', '#f0fdf4', '#fef2f2'];
                        const accentPalette = ['#0d9488', '#2563eb', '#d97706', '#a21caf', '#16a34a', '#dc2626'];
                        const bg = palette[idx % palette.length];
                        const accent = accentPalette[idx % accentPalette.length];
                        return (
                            <Col key={account.id} xs={24} sm={12} md={6}>
                                <Card style={{ borderRadius: 14, background: bg, height: '100%' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{account.account_name}</Text>
                                    <div><Text style={{ fontSize: 12 }}>{account.bank_name}</Text></div>
                                    <Title level={4} style={{ margin: '8px 0 0', color: accent }}>{formatCurrency(account.current_balance)}</Title>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* Transfers Table */}
                <Card style={{ borderRadius: 14 }} title={<Text strong>Transfer History</Text>}>
                    <Table
                        rowKey="id"
                        columns={transferColumns}
                        dataSource={transfers}
                        loading={loading}
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        scroll={{ x: true }}
                        locale={{ emptyText: 'No bank transfers found. Create your first transfer to get started.' }}
                    />
                </Card>
            </div>

            {/* New Transaction Modal */}
            <Modal
                title={
                    <Space>
                        <SwapOutlined style={{ color: '#0d9488' }} />
                        {formData.transfer_type === 'cash_deposit' ? 'Cash Deposit' :
                         formData.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal' :
                         'Bank Transfer'}
                    </Space>
                }
                open={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                width={680}
            >
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Transaction Type <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Segmented
                            block
                            value={formData.transfer_type}
                            onChange={handleTransferTypeChange}
                            options={transferTypes.map(t => ({ label: t.label, value: t.value }))}
                        />
                    </div>

                    <Row gutter={16}>
                        {(formData.transfer_type === 'transfer' || formData.transfer_type === 'cash_withdrawal') && (
                            <Col xs={24} md={12}>
                                <div className="mb-3">
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                                        {formData.transfer_type === 'cash_withdrawal' ? 'Withdraw From Bank Account' : 'From Account'}
                                        <span style={{ color: '#ff4d4f' }}> *</span>
                                    </Text>
                                    <Select
                                        value={formData.from_account_id || undefined}
                                        onChange={(value) => setFormData(prev => ({ ...prev, from_account_id: value }))}
                                        placeholder={formData.transfer_type === 'cash_withdrawal' ? 'Select Bank Account' : 'Select Source Account'}
                                        style={{ width: '100%' }}
                                    >
                                        {bankAccounts
                                            .filter(acc => acc.is_active && (formData.transfer_type === 'cash_withdrawal' ? acc.account_type !== 'Cash' : true))
                                            .map(account => (
                                                <Option key={account.id} value={String(account.id)}>
                                                    {account.account_name} ({account.bank_name}) - {formatCurrency(account.current_balance)}
                                                </Option>
                                            ))}
                                    </Select>
                                    {formData.from_account_id && (() => {
                                        const sourceAccount = getAccountInfo(parseInt(formData.from_account_id));
                                        return sourceAccount && (
                                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                                Available Balance: <Text strong style={{ color: '#16a34a' }}>{formatCurrency(sourceAccount.balance)}</Text>
                                            </Text>
                                        );
                                    })()}
                                </div>
                            </Col>
                        )}

                        {formData.transfer_type === 'cash_withdrawal' && (
                            <Col xs={24} md={12}>
                                <div className="mb-3">
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>Withdraw To</Text>
                                    <Card size="small" style={{ background: '#f8fafc' }}>
                                        <Text strong>Cash in Hand</Text>
                                        {cashAccount && (
                                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                                Current: {formatCurrency(cashAccount.current_balance)}
                                            </Text>
                                        )}
                                    </Card>
                                </div>
                            </Col>
                        )}

                        {formData.transfer_type === 'cash_deposit' && (
                            <Col xs={24} md={12}>
                                <div className="mb-3">
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>Deposit From</Text>
                                    <Card size="small" style={{ background: '#f8fafc' }}>
                                        <Text strong>Cash in Hand</Text>
                                        {cashAccount && (
                                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                                Available: {formatCurrency(cashAccount.current_balance)}
                                            </Text>
                                        )}
                                    </Card>
                                </div>
                            </Col>
                        )}

                        {(formData.transfer_type === 'transfer' || formData.transfer_type === 'cash_deposit') && (
                            <Col xs={24} md={12}>
                                <div className="mb-3">
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                                        {formData.transfer_type === 'cash_deposit' ? 'Deposit To Bank Account' : 'To Account'}
                                        <span style={{ color: '#ff4d4f' }}> *</span>
                                    </Text>
                                    <Select
                                        value={formData.to_account_id || undefined}
                                        onChange={(value) => setFormData(prev => ({ ...prev, to_account_id: value }))}
                                        placeholder={formData.transfer_type === 'cash_deposit' ? 'Select Bank Account' : 'Select Destination Account'}
                                        style={{ width: '100%' }}
                                    >
                                        {bankAccounts
                                            .filter(acc => acc.is_active &&
                                                    acc.id !== parseInt(formData.from_account_id) &&
                                                    (formData.transfer_type === 'cash_deposit' ? acc.account_type !== 'Cash' : true))
                                            .map(account => (
                                                <Option key={account.id} value={String(account.id)}>
                                                    {account.account_name} ({account.bank_name}) - {formatCurrency(account.current_balance)}
                                                </Option>
                                            ))}
                                    </Select>
                                </div>
                            </Col>
                        )}
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Transfer Date <span style={{ color: '#ff4d4f' }}>*</span></Text>
                                <DatePicker
                                    value={formData.transfer_date ? dayjs(formData.transfer_date) : null}
                                    onChange={(date) => setFormData(prev => ({ ...prev, transfer_date: date ? date.format('YYYY-MM-DD') : '' }))}
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    allowClear={false}
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Transfer Amount <span style={{ color: '#ff4d4f' }}>*</span></Text>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    value={formData.amount}
                                    onChange={(value) => setFormData(prev => ({ ...prev, amount: value }))}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Transfer Charges</Text>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    value={formData.transfer_charges}
                                    onChange={(value) => setFormData(prev => ({ ...prev, transfer_charges: value || 0 }))}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </Col>
                    </Row>

                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Description</Text>
                        <Input.TextArea
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Transfer description or notes"
                        />
                    </div>

                    {/* Transfer Summary - Memoized */}
                    {TransferSummary}

                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Space>
                            <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {loading ? 'Processing...' : 'Complete Transfer'}
                            </Button>
                        </Space>
                    </div>
                </form>
            </Modal>
        </Layout>
    );
}
